import { useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";
import api from "../../../api";
import { getBackendUrl } from "../../../util/envFn";
import { User } from "../../../types/User";
import { useUnsavedChangesWarning } from "./useUnsavedChangesWarning";

export const useUserData = (userId: string) => {
    const [userData, setUserData] = useState<User | null>(null);
    const [skills, setSkills] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editMode, setEditMode] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    // Use our custom hook to handle navigation warnings when in edit mode
    useUnsavedChangesWarning(
        editMode,
        "You have unsaved changes in your profile. Are you sure you want to leave?"
    );

    const fetchUserData = useCallback(async () => {
        if (!userId) {
            console.error("fetchUserData called with no userId");
            return;
        }

        try {
            setLoading(true);
            const response = await api.get(getBackendUrl(`users/${userId}`));

            if (response.data) {
                setUserData(response.data);
                if (response.data.skills) {
                    setSkills(response.data.skills.split(","));
                } else {
                    setSkills([]);
                }
            }
            setError(null);
        } catch (error) {
            console.error("Error fetching user data:", error);
            setError("Failed to load user data");
        } finally {
            setLoading(false);
        }
    }, [userId]);

    const updateUserData = async (updatedData: User) => {
        if (!userId) {
            console.error("updateUserData called with no userId");
            enqueueSnackbar("Cannot update: User ID is missing", { variant: "error" });
            return false;
        }

        setLoading(true);

        console.log(updatedData);

        api.patch(
            getBackendUrl(`users/update/${userId}`),
            {
                userData: {
                    ...updatedData,
                    skills: skills.join(",")
                }
            }
        ).then(res => {
            fetchUserData();
            enqueueSnackbar("Profile updated successfully", { variant: "success" });
        }).catch(err => {
            console.error("Error updating user profile:", err);
            enqueueSnackbar("Failed to update profile", { variant: "error" });
        }).finally(() => {
            setLoading(false);
            setEditMode(false);
        });
    };

    // Set initial data on mount and when userId changes
    useEffect(() => {
        if (userId) {
            fetchUserData();
        } else {
            console.warn("No userId available for fetching user data");
        }
    }, [userId, fetchUserData]);

    return {
        userData,
        setUserData,
        skills,
        setSkills,
        loading,
        error,
        fetchUserData,
        updateUserData,
        editMode,
        setEditMode,
    };
};

export default useUserData; 